import React, { useCallback, useEffect, useRef, useState } from "react";
import CommonLayout from "../../../Components/CommonLayout/CommonLayout.jsx";
import BasicTable from "../../../Components/CommonTable/BasicTable/BasicTable.jsx";
import OrderApi from "../../../App/api/order.apis";
import { useFetchAll } from "../../../custom/FetchApiHook.jsx";
import { DeliveryManApis } from "../../../App/api/delivery.apis";
import { PaystackButton } from "react-paystack";
import { toast } from "react-toastify";
import earningApis from "../../../App/api/earning.apis.js";

const formatCurrency = (val) => {
  const n = parseFloat(val) || 0;
  return `₵ ${n.toFixed(2)}`;
};

const STATUS_COLORS = {
  picked_up: "bg-cyan-100 text-cyan-700",
  delivered: "bg-green-100 text-green-700",
  assigned: "bg-indigo-100 text-indigo-700",
  accepted: "bg-red-100 text-red-700",
  pending: "bg-yellow-100 text-yellow-700",
  cancelled: "bg-red-100 text-red-700",
  rejected: "bg-red-100 text-red-700",
  returned: "bg-gray-100 text-gray-600",
  out_for_delivery: "bg-cyan-100 text-cyan-700",
};

const PAYMENT_STATUS_COLORS = {
  PAID: "bg-green-100 text-green-700",
  UNPAID: "bg-yellow-100 text-yellow-700",
  PENDING: "bg-red-100 text-red-700",
  FAILED: "bg-red-100 text-red-700",
  COD: "bg-orange-100 text-orange-700",
};

// ─── Payout History Modal ─────────────────────────────────────────────────────
const PayoutHistoryModal = ({ deliveryMan, onClose }) => {
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, totalPages: 1, totalCount: 0 });

  const fetchPayouts = async (page = 1) => {
    setLoading(true);
    try {
      const response = await earningApis.getDeliverymanPayoutById(
        { delivery_man_id: deliveryMan._id },
        { page, limit: 10, sortOrder: 'desc' }
      );
      const data = response?.data?.data || response?.data;
      setPayouts(data?.payouts || []);
      setSummary(data?.summary || null);
      setPagination((prev) => ({
        ...prev,
        page,
        totalPages: data?.totalPages || 1,
        totalCount: data?.totalCount || 0,
      }));
    } catch (error) {
      console.error('Failed to fetch payout history:', error);
      toast.error('Failed to load payout history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (deliveryMan?._id) fetchPayouts(1);
  }, [deliveryMan]);

  const METHOD_COLORS = {
    CARD: 'bg-blue-100 text-blue-700',
    BANK: 'bg-purple-100 text-purple-700',
    CASH: 'bg-green-100 text-green-700',
  };

  const STATUS_BADGE = {
    COMPLETED: 'bg-green-100 text-green-700',
    PENDING: 'bg-yellow-100 text-yellow-700',
    FAILED: 'bg-red-100 text-red-700',
  };

  return (
    <div className="fixed inset-0 z-60 flex items-start justify-center bg-black/60 overflow-y-auto"
      style={{ zIndex: 60 }}>
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 my-8">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              Payout History — {deliveryMan.firstName} {deliveryMan.lastName}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">{deliveryMan.phoneNumber}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-3 gap-4 px-6 py-4 bg-gray-50 border-b">
            <div className="bg-white rounded-xl border p-4">
              <p className="text-xs text-gray-500 mb-1">Total Paid Out</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(summary.totalPaidOut)}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <p className="text-xs text-gray-500 mb-1">Total Payouts</p>
              <p className="text-xl font-bold text-gray-800">{summary.totalPayouts}</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <p className="text-xs text-gray-500 mb-1">Total Orders Paid</p>
              <p className="text-xl font-bold text-blue-600">{summary.totalOrders}</p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <svg className="animate-spin h-8 w-8 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Loading payout history…
            </div>
          ) : payouts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-sm">No payout history found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Orders</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Method</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Reference</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Paid By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {payouts.map((payout, index) => (
                      <tr key={payout._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                          {(pagination.page - 1) * 10 + index + 1}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {payout.payout_date
                            ? new Date(payout.payout_date).toLocaleDateString('en-GB', {
                                day: '2-digit', month: 'short', year: 'numeric',
                              })
                            : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-gray-800">{payout.orders?.length || 0}</span>
                          <span className="text-gray-400 text-xs ml-1">orders</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-green-600">{formatCurrency(payout.total_amount)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${METHOD_COLORS[payout.payment_method] || 'bg-gray-100 text-gray-600'}`}>
                            {payout.payment_method}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_BADGE[payout.status] || 'bg-gray-100 text-gray-600'}`}>
                            {payout.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">
                          {payout.transaction_reference
                            ? payout.transaction_reference.slice(-10)
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-700 text-xs">
                          {payout.paid_by
                            ? `${payout.paid_by.first_name || ''} ${payout.paid_by.last_name || ''}`.trim() || payout.paid_by.email
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">
                    Showing page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} total)
                  </p>
                  <div className="flex gap-2">
                    <button
                      disabled={pagination.page <= 1}
                      onClick={() => fetchPayouts(pagination.page - 1)}
                      className="px-3 py-1.5 text-sm rounded-lg border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => fetchPayouts(pagination.page + 1)}
                      className="px-3 py-1.5 text-sm rounded-lg border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Orders Modal ─────────────────────────────────────────────────────────────
const OrdersModal = ({
  deliveryMan, orders, loading, onClose, onOrderIdClick,
  selectedOrders, setSelectedOrders, setShowPayoutModal, onViewPayoutHistory,
}) => {
  if (!deliveryMan) return null;

  const orderColumns = [
    {
      field: "select", header: "",
      accessor: (row) => {
        const isDelivered = row.status?.toLowerCase() === "delivered";
        const checked = selectedOrders.some((o) => o._id === row._id);
        return (
          <input type="checkbox" disabled={!isDelivered} checked={checked}
            className={!isDelivered ? "cursor-not-allowed opacity-50" : ""}
            onChange={(e) => {
              e.stopPropagation();
              if (!isDelivered) return;
              if (e.target.checked) {
                setSelectedOrders((prev) => [...prev, row]);
              } else {
                setSelectedOrders((prev) => prev.filter((o) => o._id !== row._id));
              }
            }}
          />
        );
      },
    },
    {
      field: "order_number", header: "Order No.",
      accessor: (row) => (
        <span className="font-mono font-bold text-gray-800">#{row.order_number || row._id?.slice(-6) || "—"}</span>
      ),
    },
    {
      field: "customer_order_id", header: "Linked Order",
      accessor: (row) => {
        const id = row.customer_order_id;
        if (!id) return "—";
        return (
          <button onClick={(e) => { e.stopPropagation(); onOrderIdClick(id); }}
            className="font-mono font-bold text-red-500 hover:text-red-700 hover:underline transition-colors">
            #{typeof id === "string" ? id.slice(-6) : "—"}
          </button>
        );
      },
    },
    {
      field: "customer", header: "Customer",
      accessor: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-800 text-sm">{row.order_details?.customerName || "—"}</span>
          {row.order_details?.customerPhone && (
            <span className="text-xs text-gray-500">{row.order_details.customerPhone}</span>
          )}
        </div>
      ),
    },
    {
      field: "store", header: "Store",
      accessor: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-800 text-sm">{row.order_details?.store?.name || "—"}</span>
          {row.order_details?.store?.address && (
            <span className="text-xs text-gray-500">{row.order_details.store.address}</span>
          )}
        </div>
      ),
    },
    {
      field: "status", header: "Status",
      accessor: (row) => {
        const status = row.status || "—";
        const cls = STATUS_COLORS[status?.toLowerCase()] || "bg-gray-100 text-gray-600";
        return (
          <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${cls}`}>
            {status.replace(/_/g, " ")}
          </span>
        );
      },
    },
    {
      field: "payment_method", header: "Pay Method",
      accessor: (row) => {
        const method = row.order_details?.payment?.method;
        const icons = { CASH: "💵", CARD: "💳", ONLINE: "🌐", COD: "💵" };
        return <span className="text-sm">{icons[method] || ""} {method || "—"}</span>;
      },
    },
    {
      field: "delivery_charge", header: "Delivery Charge",
      accessor: (row) => formatCurrency(row.order_details?.pricing?.delivery_charge),
    },
    {
      field: "total_amount", header: "Total Amount",
      accessor: (row) => (
        <span className="font-semibold text-blue-700">
          {formatCurrency(row.order_details?.pricing?.total_amount)}
        </span>
      ),
    },
    {
      field: "createdAt", header: "Date",
      accessor: (row) => row.createdAt
        ? new Date(row.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : "—",
    },
  ];

  const deliveredCount = orders.filter((o) => o.status?.toLowerCase() === "delivered").length;
  const pendingCount = orders.filter(
    (o) => !["delivered", "cancelled", "rejected"].includes(o.status?.toLowerCase())
  ).length;

  const payoutTotal = selectedOrders.reduce(
    (sum, item) => sum + (parseFloat(item?.order_details?.pricing?.delivery_charge) || 0), 0
  );

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/50 overflow-y-auto">
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-7xl mx-4 my-8">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {deliveryMan.firstName} {deliveryMan.lastName} — Orders
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {deliveryMan.phoneNumber}
              {deliveryMan.vehicleType && <> &nbsp;·&nbsp;<span className="capitalize">{deliveryMan.vehicleType}</span></>}
              {deliveryMan.vehicleDetails?.registrationNumber && <> &nbsp;·&nbsp;{deliveryMan.vehicleDetails.registrationNumber}</>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* ── Payout History Button ── */}
            <button
              onClick={onViewPayoutHistory}
              className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Payout History
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 px-6 py-4 bg-gray-50 border-b">
          <div>
            <p className="text-xs text-gray-500 mb-1">Wallet Balance</p>
            <p className="text-lg font-bold text-green-600">{formatCurrency(deliveryMan.balance)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Total Orders</p>
            <p className="text-lg font-bold text-gray-800">{orders.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Delivered</p>
            <p className="text-lg font-bold text-green-600">{deliveredCount}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">In Progress</p>
            <p className="text-lg font-bold text-orange-500">{pendingCount}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Availability</p>
            <p className={`text-sm font-semibold capitalize mt-1 ${
              deliveryMan.availability?.status === "available" ? "text-green-600"
              : deliveryMan.availability?.status === "busy" ? "text-orange-500"
              : "text-red-500"
            }`}>
              {deliveryMan.availability?.status || "offline"}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <svg className="animate-spin h-8 w-8 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Loading orders…
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <p className="text-sm">No orders found for this delivery man</p>
            </div>
          ) : (
            <>
              {selectedOrders.length > 0 && (
                <div className="flex items-center justify-between bg-white border rounded-xl p-4 mb-4 shadow-sm">
                  <div>
                    <h3 className="font-semibold text-lg">Selected Orders: {selectedOrders.length}</h3>
                    <p className="text-green-600 font-bold text-xl">{formatCurrency(payoutTotal)}</p>
                  </div>
                  <button
                    onClick={() => setShowPayoutModal(true)}
                    className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Payout
                  </button>
                </div>
              )}
              <BasicTable data={orders} columns={orderColumns} WeekRange={false} MonthRange={false} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DeliveryMan = ({ meta }) => {
  const [search, setSearch] = useState("");
  const [selectedDeliveryMan, setSelectedDeliveryMan] = useState(null);
  const [deliveryManOrders, setDeliveryManOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, sortOrder: "desc" });
  const [orderModalData, setOrderModalData] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderModalLoading, setOrderModalLoading] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");

  // ── Payout history state ───────────────────────────────────────────────────
  const [showPayoutHistory, setShowPayoutHistory] = useState(false);

  const debounceTimeout = useRef(null);

  const { value, fetchAllData } = useFetchAll(() =>
    DeliveryManApis.getAllDeliveryMan(pagination, search)
  );
  const deliveryMen = value?.data?.deliveryPersonnel || value?.data || [];

  const getDeliveryManOrders = async (deliveryPersonnelId) => {
    setOrdersLoading(true);
    setDeliveryManOrders([]);
    try {
      const response = await DeliveryManApis.getDeliveryManAllOrder({ deliveryPersonnelId });
      const raw = response?.data?.data?.orders || response?.data?.orders || response?.data?.data || response?.data || [];
      setDeliveryManOrders(Array.isArray(raw) ? raw : []);
    } catch (error) {
      console.error("Failed to fetch delivery man orders:", error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleDeliveryManPayout = async (reference = null) => {
    try {
      const payload = {
        delivery_man_id: selectedDeliveryMan?._id,
        orders: selectedOrders.map((o) => o._id),
        total_amount: selectedOrders.reduce(
          (sum, item) => sum + (parseFloat(item?.order_details?.pricing?.delivery_charge) || 0), 0
        ),
        payment_method: selectedPaymentMethod,
        transaction_reference: reference,
      };

      const response = await earningApis.createDeliveryManPayout(payload);

      if (response?.data?.success) {
        toast.success("Payout successful");
        setShowPayoutModal(false);
        setSelectedOrders([]);
        setSelectedPaymentMethod("");
        getDeliveryManOrders(selectedDeliveryMan?._id);
      }
    } catch (error) {
      console.log(error);
      toast.error("Payment failed");
    }
  };

  const handlePaystackSuccess = async (reference) => {
    await handleDeliveryManPayout(reference?.reference);
  };

  const handlePaystackClose = () => console.log("Payment Closed");

  const handleOrderIdClick = async (orderId) => {
    setOrderModalLoading(true);
    setOrderModalOpen(true);
    setOrderModalData(null);
    try {
      const response = await OrderApi.getOrderById(orderId);
      setOrderModalData(response?.data?.data || response?.data || null);
    } catch (error) {
      console.error("Failed to fetch order:", error);
    } finally {
      setOrderModalLoading(false);
    }
  };

  const columns = [
    {
      field: "id", header: "ID",
      accessor: (row) => <span className="font-mono text-xs text-gray-500">{row._id?.slice(-6) || "—"}</span>,
    },
    {
      field: "name", header: "Delivery Man",
      accessor: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-800">{`${row.firstName || ""} ${row.lastName || ""}`.trim() || "Unnamed"}</span>
          <span className="text-xs text-gray-500">{row.phoneNumber || "—"}</span>
        </div>
      ),
    },
    {
      field: "vehicleType", header: "Vehicle",
      accessor: (row) => (
        <div className="flex flex-col">
          <span className="capitalize font-medium text-gray-800">{row.vehicleType || "—"}</span>
          <span className="text-xs text-gray-500">{row.vehicleDetails?.registrationNumber || "—"}</span>
        </div>
      ),
    },
    {
      field: "availability", header: "Availability",
      accessor: (row) => {
        const status = row.availability?.status || "offline";
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
            status === "available" ? "bg-green-100 text-green-700"
            : status === "busy" ? "bg-orange-100 text-orange-700"
            : "bg-red-100 text-red-700"
          }`}>{status}</span>
        );
      },
    },
    {
      field: "ratings", header: "Rating",
      accessor: (row) => (
        <span className="text-yellow-500 font-medium">
          ★ {Number(row.ratings?.average || 0).toFixed(1)}
          <span className="text-xs text-gray-400 ml-1">({row.ratings?.count || 0})</span>
        </span>
      ),
    },
    {
      field: "balance", header: "Balance",
      accessor: (row) => <span className="font-semibold text-green-600">{formatCurrency(row.balance)}</span>,
    },
    {
      field: "action", header: "Orders",
      accessor: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setSelectedDeliveryMan(row); getDeliveryManOrders(row._id); }}
          className="px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
        >
          View Orders
        </button>
      ),
    },
  ];

  const handleSearch = useCallback((val) => setSearch(val), []);
  const debouncedSearch = useCallback((val) => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => handleSearch(val), 500);
  }, [handleSearch]);

  const onPaginatorClick = (e) => {
    const nextPage = typeof e === "number" ? e : e?.page !== undefined ? Number(e.page) : 0;
    setPagination((prev) => ({ ...prev, page: nextPage + 1, limit: e?.rows || prev.limit }));
  };

  useEffect(() => { fetchAllData(); }, [pagination, search]);

  return (
    <div className="main-content">
      <CommonLayout />

      <BasicTable
        data={deliveryMen}
        columns={columns}
        handleSearch={debouncedSearch}
        onPaginatorClick={onPaginatorClick}
        handleRowClick={(row) => { setSelectedDeliveryMan(row); getDeliveryManOrders(row._id); }}
        paginatorData={{
          page: Math.max(0, pagination.page - 1),
          rows: pagination.limit,
          totalPages: value?.data?.totalPages || 1,
          totalCount: value?.data?.totalCount || 0,
        }}
        WeekRange={false}
        MonthRange={false}
      />

      {/* Orders modal */}
      <OrdersModal
        deliveryMan={selectedDeliveryMan}
        orders={deliveryManOrders}
        loading={ordersLoading}
        onClose={() => { setSelectedDeliveryMan(null); setDeliveryManOrders([]); setSelectedOrders([]); }}
        onOrderIdClick={handleOrderIdClick}
        selectedOrders={selectedOrders}
        setSelectedOrders={setSelectedOrders}
        setShowPayoutModal={setShowPayoutModal}
        onViewPayoutHistory={() => setShowPayoutHistory(true)}
      />

      {/* Payout history modal */}
      {showPayoutHistory && selectedDeliveryMan && (
        <PayoutHistoryModal
          deliveryMan={selectedDeliveryMan}
          onClose={() => setShowPayoutHistory(false)}
        />
      )}

      {/* Payout modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Delivery Man Payout</h2>
              <button onClick={() => setShowPayoutModal(false)} className="text-2xl">×</button>
            </div>
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <p className="text-sm text-gray-500">Total Selected Orders</p>
                <h3 className="text-2xl font-bold text-green-600">
                  {formatCurrency(selectedOrders.reduce(
                    (sum, item) => sum + (parseFloat(item?.order_details?.pricing?.delivery_charge) || 0), 0
                  ))}
                </h3>
              </div>
              <div className="space-y-3">
                <label className="font-medium">Select Payment Method</label>
                <div className="flex flex-col gap-3">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="payment" onChange={() => setSelectedPaymentMethod("CARD")} />
                    Card / Paystack
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="payment" onChange={() => setSelectedPaymentMethod("BANK")} />
                    Bank Transfer
                  </label>
                </div>
              </div>
              {selectedPaymentMethod === "CARD" ? (
                <PaystackButton
                  reference={new Date().getTime().toString()}
                  email="admin@test.com"
                  amount={selectedOrders.reduce(
                    (sum, item) => sum + (parseFloat(item?.order_details?.pricing?.delivery_charge) || 0), 0
                  ) * 100}
                  currency="GHS"
                  publicKey={process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "pk_test_8889c5e2ffe1b813426f25d007f478a24c152963"}
                  text="Pay With Paystack"
                  onSuccess={handlePaystackSuccess}
                  onClose={handlePaystackClose}
                  className="w-full py-3 rounded-lg text-white font-semibold bg-green-600 hover:bg-green-700"
                />
              ) : (
                <button
                  onClick={() => handleDeliveryManPayout()}
                  disabled={!selectedPaymentMethod}
                  className={`w-full py-3 rounded-lg text-white font-semibold ${
                    selectedPaymentMethod ? "bg-green-600 hover:bg-green-700" : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  Proceed Payment
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order detail modal */}
      {orderModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-8 mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b bg-gray-50">
              <h2 className="text-2xl font-bold text-gray-800">Order #{orderModalData?.order_id || "—"}</h2>
              <button onClick={() => { setOrderModalOpen(false); setOrderModalData(null); }}
                className="p-2 rounded-lg hover:bg-gray-200 transition">✕</button>
            </div>
            {orderModalLoading ? (
              <div className="flex items-center justify-center py-24 text-gray-500">Loading order details...</div>
            ) : !orderModalData ? (
              <div className="py-20 text-center text-red-500">Failed to load order</div>
            ) : (
              <div className="p-6">
                <p className="text-gray-500">Order ID: {orderModalData?.order_id}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryMan;